/**
 *
 * The search duck. Default export is the reducer
 *
 * Action Types:
 * - SEARCH_REQUESTED
 * - SEARCH_COMPLETED
 *
 * Action Creators:
 * - searchRequested()
 * - searchCompleted()
 */

import type {
  SearchCompleted,
  SearchRequested,
  SearchState,
  PersistReducerConfig,
} from '../types';

/** @type {ActionType} */
export const SEARCH_REQUESTED = 'PB/search/SEARCH_REQUESTED';
/** @type {ActionType} */
export const SEARCH_COMPLETED = 'PB/search/SEARCH_COMPLETED';

// This is what will be persisted in the store.
export const persist: PersistReducerConfig = {
  whitelist: ['tracking'],
  reducer: 'search',
};

export const initialState = {
  tracking: {
    entry_point: undefined,
    searched_for: undefined,
  },
};

/** @ignore */
export default function search(
  state: Object = initialState,
  action: SearchRequested,
): SearchState {
  switch (action.type) {
    case SEARCH_REQUESTED:
      const { category, query } = action.payload;

      let entry_point = category ? 'cat' : 'all';
      let searched_for = query;

      return {
        ...state,
        tracking: {
          entry_point,
          searched_for,
        },
      };
    default:
      return state;
  }
}

/**
 * This action creator will return a SEARCH_REQUESTED action. Used
 * to communicate that a search has been requested.
 *
 * @param {string} query - The query string to search packages with.
 * @param {string} from - Where the search was performed from, this is a self-identifier
 *
 * @return {SearchRequested}
 * @property {string} type - SEARCH_REQUESTED
 * @property {Object} payload - The payload for search
 * @property {string} payload.query - The query string.
 * @property {string} payload.from - A string identifier annotating where the search was performed
 */

export function searchRequested(
  query: string,
  from: string,
  category?: string,
): SearchRequested {
  return {
    type: SEARCH_REQUESTED,
    payload: { category, from, query },
  };
}

/**
 * This action creator will return a SEARCH_COMPLETED action. Used
 * to communicate that a search has been completed.
 *
 * @param {string} query - The query string to search packages with.
 * @param {string} count - How many search results were returned.
 *
 * @return {SearchRequested}
 * @property {string} type - SEARCH_COMPLETED
 * @property {Object} payload - The payload for search
 *   @property {string} query - The query string.
 *   @property {string} count - The # of results that were returned with this query.
 */

export function searchCompleted(
  query: string,
  total: number,
  currentPage: number,
): SearchCompleted {
  return {
    type: SEARCH_COMPLETED,
    payload: { query, total, currentPage },
  };
}
